/**
 * fetchScenario: 画面上のコンテンツに対し、表示すべきシナリオ・アンケートを取得する。
 * US-D2-01/07/08、FR-Y1/Y3/Y4/Y5。business-logic-model.md準拠。
 * BR-U6-10: data-cvs（JSON配列）から複数CV情報を読み取る（1画面1タグ）。
 */

import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning } from "../core/errorHandler";
import { setLoggedIn } from "../core/authState";
import type { SpicaWidgetContext, SpicaWidgetCv, ResolveScenarioResult } from "../types";

/**
 * 画面種別の値ドメイン（U4 ScreenType enumと同一、BR-U8-21）。
 * WIDGET_VIEW分析イベントの集計キーとして使用するため、`data-screen-type`属性は
 * この4値のいずれかであることを入口で検証する（不正値・欠落値をfail-safeに拒否する）。
 */
const VALID_SCREEN_TYPES = [
  "CATALOG_DOWNLOAD_COMPLETE",
  "INQUIRY_COMPLETE",
  "EBOOK_VIEWER",
  "DETAIL_PAGE",
] as const;

/**
 * objectTypeの値ドメイン（spica-apiのObjectType enumと同一）。
 * spica-apiはCOMPANY/PRODUCT/CATALOGのみ対応する（ニュース等は対象外）。
 * これ以外（空文字・NEWSCOLUMN等）のCVはresolveに送っても解決対象外で、
 * サーバ側のObjectType.valueOfが例外になるため、入口で除外する。
 */
const VALID_OBJECT_TYPES = ["COMPANY", "PRODUCT", "CATALOG"] as const;

/** CVのobjectTypeがspica-apiの対応種別（COMPANY/PRODUCT/CATALOG）かを判定する。 */
function isSupportedCv(cv: SpicaWidgetCv): boolean {
  return (
    cv != null &&
    typeof cv.objectType === "string" &&
    (VALID_OBJECT_TYPES as readonly string[]).includes(cv.objectType) &&
    typeof cv.objectRootId === "string" &&
    cv.objectRootId.length > 0
  );
}

/**
 * script要素のdata-*属性からコンテキストを読み取る（BR-U6-10）。
 */
export function readWidgetContext(scriptElement: HTMLScriptElement): SpicaWidgetContext | null {
  const screenType = scriptElement.dataset.screenType;
  const cvsRaw = scriptElement.dataset.cvs;
  const previousAnswerValue = scriptElement.dataset.previousAnswerValue;

  if (!screenType || !cvsRaw) {
    logClientWarning("data-screen-type or data-cvs is missing");
    return null;
  }

  if (!(VALID_SCREEN_TYPES as readonly string[]).includes(screenType)) {
    // 不正なdata-screen-typeはタグの初期化自体を中止する（分析イベントの黒穴を防ぐ）
    logClientWarning("data-screen-type has an unrecognized value");
    return null;
  }

  let cvs: SpicaWidgetCv[];
  try {
    cvs = JSON.parse(cvsRaw) as SpicaWidgetCv[];
  } catch {
    logClientWarning("data-cvs is not valid JSON");
    return null;
  }

  if (!Array.isArray(cvs)) {
    logClientWarning("data-cvs is not an array");
    return null;
  }

  // spica-apiが対応しないobjectType（空文字・NEWSCOLUMN等）のCVを除外する。
  // 例: ニュースのお問い合わせ完了画面ではobjectTypeが空のCVが埋め込まれるが、
  // そのままresolveへ送るとサーバのObjectType.valueOfが例外になる（対象外は表示しないのが正）。
  const supportedCvs = cvs.filter(isSupportedCv);
  if (supportedCvs.length === 0) {
    // 有効なCVが1件も無い場合はresolveを呼ばずに終了する（ニュース等は通信ゼロでスキップ）。
    logClientWarning("no supported CV in data-cvs (all object types are out of scope)");
    return null;
  }

  return { screenType, cvs: supportedCvs, previousAnswerValue };
}

export interface FetchScenarioResult {
  success: boolean;
  result: ResolveScenarioResult | null;
}

/**
 * resolveScenarioを呼び出し、結果を取得する。
 * displayTrigger/displayParamsはresultにそのまま含めて返す。表示条件監視の開始（BR-U6-3）は
 * 呼び出し元（entry.ts）が displayConditionMonitor インスタンスを使って行う（H-3/H-4対応、
 * fetchScenario自体は監視を開始しない）。
 */
export async function fetchScenario(context: SpicaWidgetContext): Promise<FetchScenarioResult> {
  const response = await relayClient.resolveScenario<ResolveScenarioResult>({
    screenType: context.screenType,
    contents: context.cvs,
    previousAnswerValue: context.previousAnswerValue,
  });

  // サーバ側セッションで判定したログイン状態を反映（後続のモザイク/ログイン誘導の切替に使用）
  if (response.loggedIn !== null) {
    setLoggedIn(response.loggedIn);
  }

  if (!response.ok || !response.data) {
    const classified = classifyError(response);
    logClientWarning(`fetchScenario failed: ${classified.kind}`);
    return { success: false, result: null };
  }

  return { success: true, result: response.data };
}
